import { api } from "../../helpers/api";
import type { Tutorial } from "../types";
import { createMockAdapter } from "./mockServer";

/**
 * Tutorial mode is one switch: the shared axios instance's adapter. While a guide runs,
 * every request any page makes is answered by that guide's mock server; when it ends the
 * real adapter goes back. Pages never know which is in place.
 *
 * The guide in progress is remembered in sessionStorage so a reload lands back in it
 * (fresh sample data, first step) instead of on the real API with a sample document's
 * URL - and it is session-scoped so a new tab, or closing this one, is a clean exit.
 */

const SESSION_KEY = "glut_tutorial";

let active: Tutorial | null = null;
let realAdapter = api.defaults.adapter;

export function activeTutorial(): Tutorial | null {
  return active;
}

export function isTutorialActive(): boolean {
  return active !== null;
}

/** Points the app at the guide's sample data. Rebuilds the data from scratch every time. */
export async function installTutorial(tutorial: Tutorial) {
  const router = await tutorial.world();
  if (!active) realAdapter = api.defaults.adapter;
  api.defaults.adapter = createMockAdapter(router);
  active = tutorial;
  try {
    sessionStorage.setItem(SESSION_KEY, JSON.stringify({ id: tutorial.id }));
  } catch {
    /* storage unavailable - the guide still runs, it just will not survive a reload */
  }
}

export function uninstallTutorial() {
  if (!active) return;
  api.defaults.adapter = realAdapter;
  active = null;
  try {
    sessionStorage.removeItem(SESSION_KEY);
  } catch {
    /* nothing to clear */
  }
}

/**
 * Called once before React mounts (main.tsx), and awaited: if this tab was mid-guide when
 * it reloaded, put the mock server back before the first request (/me) can go out to the
 * real API, and put the URL back on the guide's opening page so no page asks for a sample
 * record the rebuilt data no longer holds. Resolves at once when there is no guide.
 */
export async function restoreTutorialSession(): Promise<void> {
  let id: string | undefined;
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    id = raw ? (JSON.parse(raw) as { id?: string }).id : undefined;
  } catch {
    id = undefined;
  }
  if (!id) return;
  // The guides are their own chunk too (registry.ts); only a tab mid-guide pays for it here.
  const { findTutorial } = await import("../registry");
  const tutorial = findTutorial(id);
  if (!tutorial) {
    try {
      sessionStorage.removeItem(SESSION_KEY);
    } catch {
      /* nothing to clear */
    }
    return;
  }
  await installTutorial(tutorial);
  const first = tutorial.steps[0]?.route;
  if (first && !first.includes(":") && window.location.pathname !== first) {
    window.history.replaceState(null, "", first);
  }
}
